import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { AnalyticsService } from './analytics.service';
import { LanguageService } from './language.service';
import { EVENTS_PAGE_SIZE, EventDoc } from '../models/event.model';
import {
  CityEventDetailResponse,
  CityEventSegment,
  CityEventsListResponse,
  CityEventsPage,
  toCityEventsPage,
  toEventDoc,
} from '../models/city-event.model';

/**
 * City events, against Feather's own REST API — `city-events/CONTRACT.md` §4.
 *
 * ⚠️ The structural difference from the venue path, and the one thing to understand about this
 * screen: `EventsService` reads venue events straight from Firestore through `@angular/fire`, while
 * city events come over HTTP. Two sources, one screen — the same split iOS and Android ended up
 * with. Do not put city events in Firestore, and do not move the venue path onto REST.
 *
 * A thin pass-through in the shape `AttractionsService` set (the only other thing here that calls
 * our own API): everything goes through `ApiService`, which already attaches `Authorization: Bearer`
 * and `X-Client`, and each call is wrapped in `firstValueFrom(...).catch(() => null)` so one dead
 * request cannot take the screen down. **The token is required** — these endpoints extend the same
 * controller attractions does and answer `401` without one (tracker I-15, ruled).
 *
 * Ordering, the upcoming/past split and the featured flag are all server-owned; nothing here
 * re-sorts, re-filters or truncates.
 */
@Injectable({ providedIn: 'root' })
export class CityEventsService {

  constructor(
    private apiService: ApiService,
    private languageService: LanguageService,
    private analytics: AnalyticsService
  ) {}

  /**
   * Every call here swallows its failure so the segment can show one generic string. That makes a
   * city with nothing published and a city whose feed is broken look identical — this event is the
   * only thing that tells them apart, so a whole city's programme cannot quietly stay dead.
   *
   * Reuses the already-registered `error_type` / `error_message` dimensions, and the event name and
   * `error_type` VALUES Android and iOS already send (`featured` / `list` — which section failed),
   * so the three clients aggregate as one metric. `details` is web-only: it is the id lookup neither
   * mobile app has to make (**I-35**).
   *
   * ⚠️ No `client_id` here. The venue travels in the base bundle as `venue_id` on web, because
   * `client_id` is a reserved gtag field — see `AnalyticsService`.
   */
  private reportError(errorType: 'featured' | 'list' | 'details', error: any): null {
    this.analytics.log('city_events_error', {
      error_type: errorType,
      // GA4 truncates parameter values at 100 characters; trim rather than let it happen silently.
      error_message: String(error?.status ?? error?.message ?? error ?? 'unknown').slice(0, 100)
    });
    return null;
  }

  /**
   * One page of the venue city's programme, already mapped onto `EventDoc`.
   *
   * @param shopId   The venue, as `clientData.id`. The backend resolves its city itself — never
   *                 pass a city name or id.
   * @param segment  `upcoming` (default) or `past`.
   * @param featured `true` / `false` to ask for one section, `null` for both. The featured strip and
   *                 the list below are two independent queries with two independent `hasMore`s.
   * @param offset   Rows already held for THIS section. `limit` is clamped to 50 server-side.
   *
   * ⚠️ Page from the returned `hasMore` and nothing else (**I-11**) — never from
   * `events.length % limit === 0`, which re-requests an empty page forever on an exact multiple and
   * stops early the moment a page yields fewer items than were fetched. An `offset` past the end is
   * a normal empty `200` with `hasMore: false`, not an error.
   */
  getCityEvents(
    shopId: number | string,
    options: {
      segment?: CityEventSegment;
      featured?: boolean | null;
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<CityEventsPage | null> {
    const { featured } = options;
    const limit = options.limit ?? EVENTS_PAGE_SIZE;

    let params = new HttpParams()
      .set('segment', options.segment ?? 'upcoming')
      .set('limit', String(limit))
      .set('offset', String(options.offset ?? 0));

    // Omitted entirely for "both sections" — `featured=` with an empty value is not the same request.
    if (featured !== null && featured !== undefined) {
      params = params.set('featured', featured ? '1' : '0');
    }

    return firstValueFrom(
      this.apiService.getWithParams<CityEventsListResponse>(
        `api/v3/shops/${shopId}/city-events`,
        params
      )
    )
      .then(response => toCityEventsPage(response, this.languageService.getCurrentLang()))
      // Which SECTION failed, in the values Android and iOS already send. The featured strip and the
      // list fail independently, and "the strip is dead on every venue in this city" is a different
      // story from "the whole feed is".
      .catch(err => this.reportError(featured === true ? 'featured' : 'list', err));
  }

  /**
   * One city event by its uuid — the details screen, a shared URL, a push target.
   *
   * Needed because the details screen is reached with an id alone and fetches: a city event is not
   * in Firestore, so `EventsService.getEvent` cannot find it. The screen therefore asks both sources,
   * which means **most calls to this method are expected to 404** — a venue event's id is not a city
   * event's uuid.
   *
   * ⚠️ So a `404` returns null WITHOUT reporting. It is an answer ("not a city event"), not a
   * failure, and reporting it would bury the real errors under one event per venue-event opened.
   * Everything else — 401, 5xx, a dead network — is still reported.
   */
  getCityEvent(uuid: string): Promise<EventDoc | null> {
    if (!uuid) return Promise.resolve(null);

    return firstValueFrom(
      this.apiService.get<CityEventDetailResponse>(`api/v3/city-events/${uuid}`)
    )
      .then(response =>
        response?.data ? toEventDoc(response.data, this.languageService.getCurrentLang()) : null
      )
      .catch(err => (err?.status === 404 ? null : this.reportError('details', err)));
  }
}
